import React, { FC, useEffect, useRef, useState } from "react";
import { ReactComponent as LoadingIcon } from "images/load.svg";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import {
	getBatchObjects,
	getData,
	getSearchedBatchObjects,
	getSearchedData,
} from "toolkit/steps/stepSix/stepSix.thunks";
import { useParams } from "react-router-dom";
import { List } from "./List/List";
import { Table } from "./Table/Table";
import { NoData } from "components-new/NoData/NoData";
import { IData } from "toolkit/steps/stepSix/stepSix.types";
import { PAGINATION_COUNT_STEP_SIX } from "constant";
import styles from "./Content.module.sass";
import cn from "classnames";

export const Content: FC = () => {
	const dispatch = useAppDispatch();
	const scrollTargetRef = useRef<HTMLDivElement|null>(null);
	const [load, setLoad] = useState<boolean>(false);
	const {
		data,
		searchMode,
		searchPending,
		searchedData,
		searchValue,
		formationMode,
		batchesMode,
		currentBatch,
		batchObjects,
		tableMode,
		lastPart,
		lastSearchedPart,
	} = useAppSelector((state) => state.stepSix);
	const { organizationId } = useParams();

	useEffect(() => {
		if (batchesMode) return;
		dispatch(
			getData({ organizationId: Number(organizationId), formationMode, part: 1, count: PAGINATION_COUNT_STEP_SIX }),
		);
		//eslint-disable-next-line
	}, [formationMode, searchMode, batchesMode]);

	useEffect(() => {
		if (!searchMode && batchesMode && currentBatch) {
			dispatch(
				getBatchObjects({
					organizationId: Number(organizationId),
					batchId: currentBatch?.id,
					part: 1,
					count: PAGINATION_COUNT_STEP_SIX,
				}),
			);
		}
		//eslint-disable-next-line
	}, [currentBatch, searchMode]);

	// pagination
	useEffect(() => {
		if (scrollTargetRef.current) {
			scrollTargetRef.current.addEventListener("scroll", () => checkPosition());
		}

		return () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			if (scrollTargetRef.current) scrollTargetRef.current.removeEventListener("scroll", () => checkPosition());
		};
		//eslint-disable-next-line
	}, [scrollTargetRef.current, lastPart, tableMode]);

	useEffect(() => {
		if (!searchMode && load && lastPart !== null) {
			if (batchesMode) {
				dispatch(
					getBatchObjects({
						organizationId: Number(organizationId),
						batchId: Number(currentBatch?.id),
						part: lastPart + 1,
						count: PAGINATION_COUNT_STEP_SIX,
						setLoad: () => setLoad(false),
					}),
				);
			} else {
				dispatch(
					getData({
						organizationId: Number(organizationId),
						formationMode,
						part: lastPart + 1,
						count: PAGINATION_COUNT_STEP_SIX,
						setLoad: () => setLoad(false),
					}),
				);
			}
		}

		if (searchMode && load && lastSearchedPart !== null) {
			if (batchesMode) {
				dispatch(
					getSearchedBatchObjects({
						organizationId: Number(organizationId),
						batchId: Number(currentBatch?.id),
						searchValue,
						part: lastSearchedPart + 1,
						count: PAGINATION_COUNT_STEP_SIX,
						setLoad: () => setLoad(false),
					}),
				);
			} else {
				dispatch(
					getSearchedData({
						organizationId: Number(organizationId),
						searchValue,
						formationMode,
						part: lastSearchedPart + 1,
						count: PAGINATION_COUNT_STEP_SIX,
						setLoad: () => setLoad(false),
					}),
				);
			}
		}
		//eslint-disable-next-line
	}, [load]);

	const checkPosition = () => {
		const height = scrollTargetRef.current?.scrollHeight;
		const screenHeight = scrollTargetRef.current?.offsetHeight;
		const scrolled = scrollTargetRef.current?.scrollTop;
		if (height && screenHeight && scrolled) {
			const threshold = height - screenHeight;
			const position = scrolled + screenHeight;

			if (position >= threshold && !load) setLoad(true);
		}
	};

	const localData: IData = searchMode ? searchedData : data;
	const renderContent = (): React.ReactNode => {
		return (
			<div
				data-comment-false={true}
				ref={scrollTargetRef}
				className={cn(styles.content, batchesMode ? styles["content-padding"] : "")}>
				{formationMode && localData.categorized.length ? (
					<List dataList={localData.categorized} title="Объект получил категорию" selectionButtons={true} />
				) : null}
				{formationMode && localData.repeatable.length ? (
					<List
						dataList={localData.repeatable}
						title="Повторное категорирование"
						repeatable={true}
						selectionButtons={!localData.categorized.length}
					/>
				) : null}
				{batchesMode && batchObjects.categorized.length ? <List dataList={batchObjects.categorized} /> : null}
				{batchesMode && batchObjects.repeatable.length ? (
					<List dataList={batchObjects.repeatable} repeatable={true} />
				) : null}
				{batchesMode && batchObjects.uncategorized.length ? <List dataList={batchObjects.uncategorized} /> : null}
				{!formationMode && !batchesMode && (
					<>
						{localData.categorized.length ? (
							<List dataList={localData.categorized} title="Объект получил категорию" />
						) : null}
						{localData.repeatable.length ? (
							<List dataList={localData.repeatable} title="Повторное категорирование" repeatable={true} />
						) : null}
						{localData.uncategorized.length ? (
							<List dataList={localData.uncategorized} title="Категорирование не завершено" />
						) : null}
					</>
				)}
			</div>
		);
	};

	return (
		<>
			{!searchMode &&
				((!batchesMode && (data.categorized.length || data.uncategorized.length || data.repeatable.length)) ||
				(batchesMode &&
					(batchObjects.categorized.length || batchObjects.uncategorized.length || batchObjects.repeatable.length)) ? (
					tableMode ? (
						<Table />
					) : (
						renderContent()
					)
				) : batchesMode ? (
					<NoData title="Похоже, что объекты, которые входили в эту партию документов, уже удалены. Вы всё ещё можете повторно скачать эту партию." />
				) : (
					<NoData title="Данные отсутствуют" />
				))}
			{searchMode &&
				(searchPending ? (
					<div className="loading-wrp">
						<LoadingIcon className="loading-icon" />
					</div>
				) : (
					<>
						{searchedData.categorized.length || searchedData.repeatable.length || searchedData.uncategorized.length ? (
							tableMode ? (
								!formationMode ? (
									<Table />
								) : searchedData.categorized.length || localData.repeatable.length ? (
									<Table />
								) : (
									<NoData title="Ничего не найдено" />
								)
							) : (
								renderContent()
							)
						) : (
							<NoData title="Ничего не найдено" />
						)}
					</>
				))}
		</>
	);
};
